import React, { useEffect, useState } from 'react';
import { api } from '../contexts/AuthContext';
import { Card } from './ui/card';
import { Moon, Sunrise, Sun, Sunset, Clock } from 'lucide-react';

const ICONS = {
  moon: Moon,
  sunrise: Sunrise,
  sun: Sun,
  sunset: Sunset,
};

const HourlyBreakdownCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const res = await api.get('/dashboard/hourly-breakdown?days=7');
        if (!cancelled) {
          setData(res.data);
          setError(false);
        }
      } catch (e) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    const t = setInterval(fetchData, 5 * 60 * 1000); // refresh ogni 5 min
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (error) return null;

  return (
    <Card className="p-4 h-full flex flex-col" data-testid="hourly-breakdown-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Clock className="h-4 w-4 text-blue-600" />
            Fasce orarie apertura
          </div>
          <div className="text-xs text-slate-500 mt-0.5">Ultimi 7 giorni · ora locale</div>
        </div>
        {data?.peak_bucket && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Picco</div>
            <div className="text-sm font-bold text-blue-700">{data.peak_bucket.label}</div>
            <div className="text-[10px] text-slate-500">{data.peak_bucket.range}</div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-6 text-center">Caricamento...</div>
      ) : (
        <>
          <div className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="hourly-total">
            {data.total_tickets}
            <span className="text-xs text-slate-500 font-normal ml-2">ticket totali</span>
          </div>

          <div className="space-y-2 flex-1">
            {data.buckets.map(b => {
              const Icon = ICONS[b.icon] || Clock;
              const isPeak = data.peak_bucket && b.id === data.peak_bucket.id && b.count > 0;
              return (
                <div key={b.id} className="group" data-testid={`hourly-bucket-${b.id}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <div className="flex items-center gap-1.5">
                      <Icon className={`h-3.5 w-3.5 ${isPeak ? 'text-blue-600' : 'text-slate-400'}`} />
                      <span className={`${isPeak ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                        {b.label}
                      </span>
                      <span className="text-slate-400 font-mono">{b.range}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono font-semibold text-slate-900 tabular-nums">{b.count}</span>
                      <span className="text-slate-400 tabular-nums w-10 text-right">{b.pct.toFixed(0)}%</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
                    <div
                      className={`h-full rounded transition-all ${isPeak ? 'bg-blue-600' : 'bg-slate-400'}`}
                      style={{ width: `${b.pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
};

export default HourlyBreakdownCard;
